// Nazorg adopter portal — the platform↔backoffice contract for the aftercare questionnaire.
//
// Two public, token-authenticated endpoints (no bearer; the path token IS the credential):
//   GET  /api/platform/nazorg/{token}             → the questionnaire definition + read-only dier context
//   POST /api/platform/nazorg/{token}/inzending   → the adopter's answers
//
// The UI is driven by a flat array of questions (the design handoff's `Vraag` model): each question
// carries its own section grouping, so the portal can render a step-by-step OR a one-page flow off the
// same data. The questions come from the backoffice wizard in production (snapshotted at invite time);
// the client only renders, collects and submits. We reuse the Refine dataProvider.custom so the base
// URL / dev-proxy / error-typing conventions match the rest of the app.

import type { HttpError } from "@refinedev/core";

import { dataProvider } from "../dataProvider";

/**
 * The answer kinds the portal can render (handoff "Datamodel"). `date` and `photo` mirror the
 * backoffice wizard's "Datum" and "Foto-upload" field types; a `photo` answer stores the uploaded
 * image URL (see {@link uploadNazorgFoto}).
 */
export type NazorgVraagType =
  | "scale"
  | "single"
  | "multi"
  | "yesno"
  | "text"
  | "number"
  | "date"
  | "photo";

/** Which flow the questionnaire is presented in. */
export type NazorgWeergave = "stap" | "pagina";

/** One question. The UI is a pure function of an array of these (handoff "Datamodel"). */
export interface NazorgVraag {
  /** Unique; the key the answer is stored under. */
  id: string;
  /** Section title, e.g. "Gezondheid & verzorging". */
  section: string;
  /** True on the first question of a new section (drives section headers + the stepper). */
  sectionStart: boolean;
  type: NazorgVraagType;
  /** Question text; may contain the `{naam}` placeholder (= the animal's name). */
  q: string;
  /** Optional sub-text under the question. */
  helper?: string;
  /** For single / multi / yesno. */
  options?: string[];
  /** For scale: 5 labels, low → high. */
  levels?: string[];
  /** For text / number. */
  placeholder?: string;
}

/**
 * One answer keyed by question id:
 *   scale → number 1–5 · single/yesno → option string · multi → string[] · text/number → string.
 */
export type NazorgAntwoord = number | string | string[] | undefined;

/** The full payload the platform serves for a token: read-only context + the question array. */
/**
 * De huisstijl van het asiel: kleuren, letter, teksten en logo, ingesteld in zijn eigen backoffice.
 * Elk veld mag ontbreken; de pagina valt dan terug op haar eigen opmaak.
 */
export interface NazorgHuisstijl {
  primair?: string;
  accent?: string;
  achtergrond?: string;
  oppervlak?: string;
  tekst?: string;
  lettertype?: "SYSTEEM" | "SCHREEF" | "ROND" | "SMAL";
  kopTitel?: string;
  welkomTekst?: string;
  slotTekst?: string;
  /** Pad naar het logo, met het token erin; afwezig als het asiel er geen heeft. */
  logoUrl?: string;
}

export interface NazorgWizard {
  /** De huisstijl van het asiel; afwezig betekent: de standaardopmaak van dit portaal. */
  huisstijl?: NazorgHuisstijl;
  /** The animal's name — fills `{naam}` in question text (default falls back to "je nieuwe huisdier"). */
  dierNaam: string;
  /** Issuing organization name — shown in the header. */
  opvangNaam: string;
  /** Optional welcome lede override; falls back to the i18n default. */
  intro?: string;
  /**
   * Optional photo of the animal (welcome card + context panel). The backoffice ships it with the
   * invitation (stored temporarily until the form is filled); a filled link 410s, so it is only ever
   * served while answerable. Absent → the portal shows a placeholder.
   */
  dierFotoUrl?: string;
  /** Optional contact phone shown in the step-flow context panel. */
  telefoon?: string;
  /** Optional shelter contact e-mail, shown next to the phone in the context panel. */
  opvangEmail?: string;
  /** Optional per-org accent (hex); overrides the default terracotta when present. */
  accent?: string;
  /** Step-by-step (default) or all-on-one-page. */
  weergave: NazorgWeergave;
  vragen: NazorgVraag[];
}

export interface NazorgInzending {
  antwoorden: Record<string, NazorgAntwoord>;
}

function path(token: string, suffix = ""): string {
  return `/platform/nazorg/${encodeURIComponent(token)}${suffix}`;
}

/** Fetch the questionnaire definition + dier context for an invitation token. */
export async function fetchNazorgWizard(token: string): Promise<NazorgWizard> {
  const result = await dataProvider.custom!<NazorgWizard>({ url: path(token), method: "get" });
  return result.data;
}

/**
 * Upload one photo for a `photo` question and resolve to the stored image URL (kept as the answer).
 * Multipart, so it bypasses the JSON dataProvider; the portal is token-authed (no bearer). Rejects
 * with a Refine-style HttpError (statusCode) on failure so the caller can distinguish expired links.
 */
export async function uploadNazorgFoto(token: string, file: File): Promise<string> {
  const base = dataProvider.getApiUrl();
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${base}${path(token, "/foto")}`, { method: "post", body: form });
  if (!response.ok) {
    throw { message: response.statusText || `HTTP ${response.status}`, statusCode: response.status };
  }
  const data = (await response.json()) as { url: string };
  return data.url;
}

/** Submit the adopter's answers. Resolves on success; rejects with a Refine HttpError otherwise. */
export async function submitNazorgInzending(
  token: string,
  inzending: NazorgInzending,
): Promise<void> {
  await dataProvider.custom!({ url: path(token, "/inzending"), method: "post", payload: inzending });
}

/** Narrow an unknown rejection to a Refine HttpError status (used to distinguish "link expired" from network). */
export function statusOf(error: unknown): number | undefined {
  return (error as HttpError | undefined)?.statusCode;
}
